import { BaseSyntheticEvent, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import clsx from 'clsx';
import { SubscribeSection } from 'types/studio/studio';

import InputV2 from 'shared/ui/InputV2';
import { useKlavioEmailSubscription } from 'shared/hooks/useKlavioEmailSubscription';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { Spinner, Button } from '@ag1/ui';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';

import styles from './SubscribeSectionForm.module.scss';

type Props = { className?: string } & Pick<
  SubscribeSection,
  'placeholder' | 'buttonLabel' | 'successButtonLabel' | 'errorMessage' | 'theme' | '_type'
>;

export const SubscribeSectionForm = (props: Props) => {
  const {
    buttonLabel,
    successButtonLabel,
    placeholder,
    _type: sectionType,
    errorMessage,
    theme,
    className
  } = props;

  const { isPending, control, handleFormSubmit, isSuccess } = useKlavioEmailSubscription({
    location: sectionType,
    errorMessage,
    eventTitleProp: placeholder
  });

  const handleOneShotFormSubmit = useCallback(
    (event: BaseSyntheticEvent) => {
      if (!isSuccess && !isPending) {
        return handleFormSubmit(event);
      }

      event.preventDefault();
      return false; // for eslint consistent return
    },
    [handleFormSubmit, isSuccess, isPending]
  );

  return (
    <div className={clsx(styles.root, className)}>
      <form className={styles.form} onSubmit={handleOneShotFormSubmit}>
        <Controller
          control={control}
          name="email"
          render={({ field, formState: { errors } }) => (
            <InputV2
              {...field}
              label={placeholder}
              disabled={isPending}
              errors={errors}
              aria-invalid={!!errors.email}
              className={clsx(
                styles.input,
                theme === 'new' && !errors.email && styles.newThemInputHover
              )}
              type="text"
              inputMode="email"
            />
          )}
        />

        <Button
          type="submit"
          variant={isSuccess ? 'outline' : 'primary'}
          size="medium"
          theme={theme}
          disabled={isPending || isSuccess}
          className={clsx(styles.button, getThemeCSSClass(theme), {
            [styles.buttonIsLoading]: isPending,
            [styles.buttonIsSuccess]: isSuccess
          })}>
          {isPending && (
            <Spinner
              overlayClassName={styles.buttonSpinner}
              color="brand-white"
              size={24}
              absolute
            />
          )}
          <div className={styles.buttonText}>
            {!isSuccess && <CustomPortableText value={buttonLabel} />}
            {isSuccess && <CustomPortableText value={successButtonLabel} />}
          </div>
        </Button>
      </form>
    </div>
  );
};
